import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  generalNotificationHistoryApi,
  previewGeneralNotificationApi,
  sendGeneralNotificationApi,
} from "src/DAL/GeneralNotification/generalNotification";
import AudienceSelector, {
  buildAudienceTarget,
  INITIAL_AUDIENCE,
} from "./components/AudienceSelector";
import SentNotificationsTable from "./components/SentNotificationsTable";

const TITLE_MAX = 120;
const MESSAGE_MAX = 1000;
const PREVIEW_DEBOUNCE_MS = 500;
const DEFAULT_ROWS_PER_PAGE = 10;

const membersLabel = (count) => `${count} member${count === 1 ? "" : "s"}`;

const cardSx = { p: { xs: 2, md: 3 }, mb: 3, borderRadius: "12px" };

/** Debounced recipient preview for the current audience target. */
function useAudiencePreview(target) {
  const [preview, setPreview] = useState({
    isLoading: false,
    count: null,
    sample: [],
    error: "",
  });
  const requestRef = useRef(0);
  const targetKey = target ? JSON.stringify(target) : "";

  useEffect(() => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (!targetKey) {
      setPreview({ isLoading: false, count: null, sample: [], error: "" });
      return undefined;
    }
    setPreview((prev) => ({ ...prev, isLoading: true, error: "" }));
    const timer = setTimeout(async () => {
      const result = await previewGeneralNotificationApi(JSON.parse(targetKey));
      if (requestRef.current !== requestId) return;
      if (result.code === 200) {
        setPreview({
          isLoading: false,
          count: result.count ?? 0,
          sample: result.sample || [],
          error: "",
        });
      } else {
        setPreview({
          isLoading: false,
          count: null,
          sample: [],
          error: result.message || "Couldn't count recipients",
        });
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [targetKey]);

  return preview;
}

/** Paged "Sent notifications" history. */
function useSentHistory() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [state, setState] = useState({
    items: [],
    total: 0,
    isLoading: true,
    error: "",
  });

  const load = useCallback(async () => {
    setState((prev) => ({ ...prev, isLoading: true, error: "" }));
    const result = await generalNotificationHistoryApi(page, rowsPerPage);
    if (result.code === 200) {
      setState({
        items: result.items || [],
        total: result.total || 0,
        isLoading: false,
        error: "",
      });
    } else {
      setState({
        items: [],
        total: 0,
        isLoading: false,
        error: result.message || "Couldn't load sent notifications",
      });
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    load();
  }, [load]);

  const changeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(0);
  };

  return { ...state, page, rowsPerPage, setPage, changeRowsPerPage, reload: load };
}

function PreviewSummary({ target, preview }) {
  if (!target) {
    return (
      <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
        Choose who should receive this notification.
      </Typography>
    );
  }
  if (preview.isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size={16} />
        <Typography sx={{ fontSize: 14 }}>Counting recipients…</Typography>
      </Box>
    );
  }
  if (preview.error) {
    return (
      <Typography sx={{ color: "error.main", fontSize: 14 }}>
        {preview.error}
      </Typography>
    );
  }
  if (preview.count === null) return null;
  if (preview.count === 0) {
    return (
      <Typography sx={{ color: "warning.dark", fontSize: 14 }}>
        No members match this audience.
      </Typography>
    );
  }
  const extra = preview.count - preview.sample.length;
  const sampleText = preview.sample.length
    ? `e.g. ${preview.sample.join(", ")}${extra > 0 ? ` and ${extra} more` : ""}`
    : "";
  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
        This will reach {membersLabel(preview.count)}
      </Typography>
      {sampleText ? (
        <Typography sx={{ color: "text.secondary", fontSize: 13 }}>
          {sampleText}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function GeneralNotifications() {
  const { enqueueSnackbar } = useSnackbar();
  const [audience, setAudience] = useState(INITIAL_AUDIENCE);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const target = useMemo(() => buildAudienceTarget(audience), [audience]);
  const preview = useAudiencePreview(target);
  const history = useSentHistory();

  const canSend =
    Boolean(target) &&
    title.trim().length > 0 &&
    message.trim().length > 0 &&
    !preview.isLoading &&
    preview.count > 0 &&
    !isSending;

  const handleSend = async () => {
    setIsSending(true);
    const result = await sendGeneralNotificationApi({
      title: title.trim(),
      message: message.trim(),
      target,
    });
    setIsSending(false);
    setIsConfirmOpen(false);
    if (result.code === 200) {
      enqueueSnackbar(
        `Notification sent to ${membersLabel(result.sentCount || 0)}`,
        { variant: "success" }
      );
      setTitle("");
      setMessage("");
      if (history.page === 0) {
        history.reload();
      } else {
        history.setPage(0);
      }
    } else {
      enqueueSnackbar(result.message || "Couldn't send the notification", {
        variant: "error",
      });
    }
  };

  return (
    <div className="container">
      <div className="row mobile-margin display-flex">
        <div className="col-12">
          <h2>General Notifications</h2>
          <Typography sx={{ color: "text.secondary", mb: 2 }}>
            Send an in-app notification to members. They see it instantly in
            the client portal and in their notification list.
          </Typography>
        </div>
      </div>

      <Card sx={cardSx}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Send notification
        </Typography>

        <AudienceSelector
          audience={audience}
          onChange={setAudience}
          disabled={isSending}
        />

        <Box sx={{ mt: 2, mb: 3 }}>
          <PreviewSummary target={target} preview={preview} />
        </Box>

        <TextField
          fullWidth
          label="Title"
          value={title}
          disabled={isSending}
          onChange={(event) => setTitle(event.target.value.slice(0, TITLE_MAX))}
          inputProps={{ maxLength: TITLE_MAX }}
          helperText={`${title.length}/${TITLE_MAX}`}
          FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Message"
          value={message}
          disabled={isSending}
          onChange={(event) =>
            setMessage(event.target.value.slice(0, MESSAGE_MAX))
          }
          inputProps={{ maxLength: MESSAGE_MAX }}
          helperText={`${message.length}/${MESSAGE_MAX}`}
          FormHelperTextProps={{ sx: { textAlign: "right", mx: 0 } }}
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            disabled={!canSend}
            onClick={() => setIsConfirmOpen(true)}
          >
            Send notification
          </Button>
        </Box>
      </Card>

      <Card sx={cardSx}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Sent notifications
        </Typography>
        <SentNotificationsTable
          items={history.items}
          total={history.total}
          page={history.page}
          rowsPerPage={history.rowsPerPage}
          isLoading={history.isLoading}
          error={history.error}
          onPageChange={history.setPage}
          onRowsPerPageChange={history.changeRowsPerPage}
          onRetry={history.reload}
        />
      </Card>

      <Dialog
        open={isConfirmOpen}
        onClose={() => (isSending ? null : setIsConfirmOpen(false))}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          Send to {membersLabel(preview.count || 0)}?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontWeight: 600 }}>{title.trim()}</Typography>
          <Typography
            sx={{ color: "text.secondary", whiteSpace: "pre-wrap", mt: 0.5 }}
          >
            {message.trim()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={isSending} onClick={() => setIsConfirmOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" disabled={isSending} onClick={handleSend}>
            {isSending ? "Sending…" : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
